import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FilePenLine,
  Gavel,
  Loader2,
  Play,
} from 'lucide-react';
import { isLaborPortalHostname } from '../../../constants';
import {
  fetchJudicatureDemoArtifact,
  getJudicatureDemoJob,
  JudicatureJobApiError,
  startJudicatureDemoJob,
  uploadJudicatureDemoDocxToDocuments,
  type JudicatureArtifactFormat,
  type JudicatureLitigationJob,
} from '../judicatureLitigationJobs';
import type { PortalDocumentOpenFields } from '../portalDocuments';

export interface JudicatureDemoJobCardProps {
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  onDocumentReady: (fields: PortalDocumentOpenFields) => void | Promise<void>;
}

const STATUS_LABELS: Record<JudicatureLitigationJob['status'], string> = {
  queued: 'Wartet',
  running: 'Analyse läuft',
  succeeded: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
};

function errorLabel(error: unknown): string {
  if (error instanceof JudicatureJobApiError) return error.code;
  return error instanceof Error ? error.message : 'UNBEKANNTER_FEHLER';
}

export const JudicatureDemoJobCard: React.FC<JudicatureDemoJobCardProps> = ({
  customerId,
  customerStatus,
  customerEmail,
  onDocumentReady,
}) => {
  const [job, setJob] = React.useState<JudicatureLitigationJob | null>(null);
  const [error, setError] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [artifactBusy, setArtifactBusy] = React.useState<string | null>(null);

  const isLabor =
    typeof window !== 'undefined' && isLaborPortalHostname(window.location.hostname);

  React.useEffect(() => {
    if (!job || job.status === 'succeeded' || job.status === 'failed') return undefined;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const next = await getJudicatureDemoJob(job.job_id);
        if (cancelled) return;
        setJob(next);
        setError('');
        if (next.status !== 'succeeded' && next.status !== 'failed') {
          timer = window.setTimeout(poll, 3_000);
        }
      } catch (err) {
        if (cancelled) return;
        setError(errorLabel(err));
        timer = window.setTimeout(poll, 5_000);
      }
    };

    timer = window.setTimeout(poll, 2_000);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [job?.job_id]);

  if (!isLabor) return null;

  const startDemo = async () => {
    setStarting(true);
    setError('');
    try {
      setJob(await startJudicatureDemoJob(customerId));
    } catch (err) {
      setError(errorLabel(err));
    } finally {
      setStarting(false);
    }
  };

  const downloadArtifact = async (format: JudicatureArtifactFormat) => {
    if (!job) return;
    setArtifactBusy(`download-${format}`);
    setError('');
    try {
      const artifact = await fetchJudicatureDemoArtifact(job.job_id, format);
      const href = URL.createObjectURL(artifact.blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = artifact.filename;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(href), 1_000);
    } catch (err) {
      setError(errorLabel(err));
    } finally {
      setArtifactBusy(null);
    }
  };

  const openDocx = async () => {
    if (!job) return;
    setArtifactBusy('open-docx');
    setError('');
    try {
      const fields = await uploadJudicatureDemoDocxToDocuments({
        jobId: job.job_id,
        customerId,
        customerStatus,
        customerEmail,
      });
      await onDocumentReady(fields);
    } catch (err) {
      setError(errorLabel(err));
    } finally {
      setArtifactBusy(null);
    }
  };

  const artifactFormats = new Set(job?.artifacts?.map((item) => item.format) ?? []);
  const terminal = job?.status === 'succeeded' || job?.status === 'failed';

  return (
    <section className="rounded-2xl border border-cyan-500/30 bg-cyan-950/20 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-cyan-500/15 p-2.5">
          <Gavel className="h-5 w-5 text-cyan-300" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-white">OGH-Judikatur / Fallanalyse</h2>
            <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cyan-200">
              Labor-Demo
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-300">
            Quellengebundener Premium-Job auf LEXCORE-Norm und OGH-Judikatur. Das ist keine
            LEXCORE-Normensuche.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700/70 bg-slate-900/70 p-3 text-xs text-slate-300">
        <p className="font-semibold text-slate-200">Fixe synthetische Demo-Fakten</p>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Schriftliches Enddatum in einem fiktiven Mietvertrag</li>
          <li>Keine dokumentierte Anerkennung eines unbefristeten Mietverhältnisses</li>
        </ul>
        <p className="mt-2 text-slate-500">Keine echten Akten und keine personenbezogenen Daten eingeben.</p>
      </div>

      {job && (
        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {job.status === 'succeeded' ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-hidden />
              ) : job.status === 'failed' ? (
                <AlertTriangle className="h-4 w-4 text-red-400" aria-hidden />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-cyan-300" aria-hidden />
              )}
              <span className="text-sm font-bold text-white">{STATUS_LABELS[job.status]}</span>
            </div>
            <span className="font-mono text-[10px] text-slate-500">
              Versuch {job.attempt}/{job.max_attempts}
            </span>
          </div>
          <p className="mt-2 break-all font-mono text-[10px] text-slate-500">{job.job_id}</p>

          {job.status === 'failed' && (
            <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-2">
              <p className="font-mono text-xs font-bold text-red-300">
                {job.error_code || 'JOB_FAILED'}
              </p>
              {job.gate_violations?.map((item, index) => (
                <p key={`${item.code}-${index}`} className="mt-1 font-mono text-[10px] text-red-200/80">
                  {item.code}{item.section ? ` · ${item.section}` : ''}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2">
          <p className="font-mono text-xs font-bold text-red-300">{error}</p>
        </div>
      )}

      {job?.status === 'succeeded' ? (
        <div className="grid gap-2 sm:grid-cols-3">
          {(['docx', 'pdf'] as const).map((format) => (
            <button
              key={format}
              type="button"
              disabled={!artifactFormats.has(format) || artifactBusy !== null}
              onClick={() => void downloadArtifact(format)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2.5 text-xs font-bold text-cyan-100 transition-colors hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {artifactBusy === `download-${format}` ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
              {format.toUpperCase()}
            </button>
          ))}
          <button
            type="button"
            disabled={!artifactFormats.has('docx') || artifactBusy !== null}
            onClick={() => void openDocx()}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-3 py-2.5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
          >
            {artifactBusy === 'open-docx' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FilePenLine className="h-4 w-4" aria-hidden />
            )}
            Weiterbearbeiten
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={starting || (!!job && !terminal)}
          onClick={() => void startDemo()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 text-sm font-bold text-slate-950 transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
        >
          {starting || (!!job && !terminal) ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Play className="h-4 w-4" aria-hidden />
          )}
          {job?.status === 'failed' ? 'Demo erneut starten' : 'Synthetische Fallanalyse starten'}
        </button>
      )}

      <p className="text-[10px] leading-relaxed text-slate-500">
        Jeder Export trägt HUMAN_REVIEW_REQUIRED. Das System leistet keine Rechtsberatung;
        die anwaltliche Prüfung bleibt zwingend.
      </p>
    </section>
  );
};
